"use client";;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:ENCON_SENTRY_KEY":true});}catch(e){}}();
import type React from "react";
import { useEffect, useRef, useState } from "react";
import Container from "@/components/Container";
import { CMSLink } from "@/components/Link";
import RichText from "@/components/RichText";
import type { ContentBlock as ContentBlockProps } from "@/payload-types";
import "./index.scss";
import { Link } from "@/i18n/routing";

const createAnchorId = (text: string) => {
	return text
		.toLowerCase()
		.replace(/\s+/g, "-")
		.replace(/[^a-z0-9-]/g, "");
};

export const ContentBlock: React.FC<ContentBlockProps> = (props) => {
	const {
		richText,
		enableLink,
		link,
		size,
		enableTable,
		showCircle,
		alignment,
	} = props;

	const [activeAnchor, setActiveAnchor] = useState<string>("");
	const contentRichTextRef = useRef<HTMLDivElement>(null);

	// Extract h2 texts for table of contents
	const h2Texts =
		richText?.root?.children
			?.filter((child: any) => child.tag === "h2")
			.map((heading: any) => heading.children?.[0]?.text)
			.filter(Boolean) || [];

	useEffect(() => {
		const assignHeadingIds = () => {
			const root = contentRichTextRef.current;
			if (!root) return;
			root.querySelectorAll<HTMLHeadingElement>("h2").forEach((h2) => {
				if (h2.textContent) h2.id = createAnchorId(h2.textContent);
			});
		};

		assignHeadingIds();
		const observer = new MutationObserver(assignHeadingIds);
		if (contentRichTextRef.current) {
			observer.observe(contentRichTextRef.current, {
				childList: true,
				subtree: true,
			});
		}
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const handleAnchorClick = (e: Event) => {
			const target = e.target as HTMLAnchorElement;
			if (target.href?.includes("#")) {
				e.preventDefault();
				const id = target.href.split("#")[1];
				setActiveAnchor(id);
				const element = document.getElementById(id);
				if (element) {
					const yOffset = -150; // Offset for sticky header
					const y =
						element.getBoundingClientRect().top + window.pageYOffset + yOffset;
					window.scrollTo({ top: y, behavior: "smooth" });
				}
			}
		};

		// Add event listener to all anchor links in the table
		const tableLinks = document.querySelectorAll(".table a");
		tableLinks.forEach((link) => {
			link.addEventListener("click", handleAnchorClick);
		});

		// Cleanup
		return () => {
			tableLinks.forEach((link) => {
				link.removeEventListener("click", handleAnchorClick);
			});
		};
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			const headings = h2Texts.map((text) => ({
				id: createAnchorId(text),
				element: document.getElementById(createAnchorId(text)),
			}));

			// Find the first heading currently in view (same approach as /cases)
			const offset = 200; // adjust to match sticky header height as needed
			for (let i = headings.length - 1; i >= 0; i--) {
				const heading = headings[i];
				if (heading.element) {
					const rect = heading.element.getBoundingClientRect();
					if (rect.top <= offset) {
						setActiveAnchor(heading.id);
						break;
					}
				}
			}
		};

		window.addEventListener("scroll", handleScroll);
		handleScroll(); // initial state

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [h2Texts]);

	return (
		<div
			className={`content-block ${showCircle ? "circle" : ""} ${alignment} ${enableLink ? "cta" : ""}`}
		>
			<Container>
				<div className="content-block-grid">
					<div className={`grid-col grid-col-${size}`}>
						{enableTable ? (
							<div className="rich_text" ref={contentRichTextRef}>
								{h2Texts.length > 0 && (
									<div className="table">
										{h2Texts.map((heading, index) => (
											// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
											<Link
												// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
												key={index}
												href={`#${createAnchorId(heading)}`}
												className={
													activeAnchor === createAnchorId(heading)
														? "active"
														: ""
												}
											>
												{heading}
											</Link>
										))}
									</div>
								)}
								{richText && <RichText data={richText} />}
								{enableLink && <CMSLink {...link} />}
							</div>
						) : (
							<div>
								{richText && <RichText data={richText} />}
								{enableLink && <CMSLink {...link} />}
							</div>
						)}
					</div>
				</div>
			</Container>
		</div>
	);
};
